import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Gift, Mail, MapPin, Phone, Sparkles, Star } from "lucide-react";
import { toast } from "sonner";
import { CHAPTERS, MEDIA, PACKAGES } from "../data";
import { MaskedLine, Reveal } from "../components/Reveal";
import { api, formatApiError } from "../context/AuthContext";

export function Stars({ size = 14 }) {
  return (
    <span className="inline-flex gap-0.5 text-amber-400">
      {[...Array(5)].map((_, i) => (
        <Star key={i} size={size} fill="currentColor" strokeWidth={0} />
      ))}
    </span>
  );
}

export function Eyebrow({ children, center = false }) {
  return (
    <p className={`flex items-center gap-4 text-[11px] uppercase tracking-[0.4em] text-amber-400/90 font-mono-gold mb-5 ${center ? "justify-center" : ""}`}>
      <span className="h-px w-10 bg-amber-400/70 inline-block" />
      {children}
      {center && <span className="h-px w-10 bg-amber-400/70 inline-block" />}
    </p>
  );
}

function Hero() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div style={{ y: videoY, scale: videoScale }} className="absolute inset-0">
        <video
          className="w-full h-full object-cover"
          src={MEDIA.heroVideo}
          poster={MEDIA.heroPoster}
          autoPlay
          muted
          loop
          playsInline
          data-testid="hero-video"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-[#09090B]/60 to-[#09090B]/30" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(212,175,55,0.14),transparent_55%)]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 pt-44 w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 1 }}
          data-testid="hero-eyebrow"
        >
          <Eyebrow>Mobile Concierge Detailing — At Your Driveway</Eyebrow>
        </motion.div>
        <h1 className="font-serif-lux font-medium tracking-tight leading-[0.98] text-6xl sm:text-8xl lg:text-9xl" data-testid="hero-heading">
          <MaskedLine text="Valet" delay={0.15} />
          <MaskedLine text="Premier" delay={0.3} className="gold-text italic" />
          <MaskedLine text="Detailing" delay={0.45} />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-6 max-w-xl text-zinc-300 text-base sm:text-lg leading-relaxed"
          data-testid="hero-subtitle"
        >
          High-art automotive perfection, delivered where your car parks. Foam bath to ceramic cure — you never leave home.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <button
            onClick={() => navigate("/book")}
            data-testid="hero-book-now-button"
            className="gold-foil text-black font-semibold uppercase tracking-[0.25em] text-[11px] px-9 py-4 rounded-sm hover:scale-[1.03] active:scale-[0.98] transition-transform duration-200 flex items-center gap-2"
          >
            Book Mobile Service <ArrowRight size={14} />
          </button>
          <button
            onClick={() => document.querySelector("#packages")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="hero-explore-packages-button"
            className="border border-amber-200/50 text-amber-100 font-medium tracking-[0.15em] text-sm px-9 py-4 rounded-sm hover:bg-amber-500/10 transition-colors duration-200"
          >
            View packages
          </button>
          <button
            onClick={() => navigate("/gift-cards")}
            data-testid="hero-gift-card-button"
            className="text-zinc-300 hover:text-amber-300 font-semibold uppercase tracking-[0.25em] text-[11px] px-6 py-4 flex items-center gap-2 transition-colors"
          >
            <Gift size={14} /> Send a Gift Card
          </button>
        </motion.div>
      </div>
    </section>
  );
}

function Marquee() {
  const items = ["Snow-Foam Decontamination", "Ceramic Preservation", "Mobile Concierge", "Wheel & Caliper Ritual", "Interior Steam Revival", "Obsidian Finish"];
  const row = [...items, ...items];
  return (
    <div data-testid="editorial-marquee" className="border-y border-amber-500/15 bg-[#0c0c0e] py-6 overflow-hidden">
      <div className="marquee-track flex whitespace-nowrap gap-14 w-max">
        {row.map((t, i) => (
          <span key={i} className="font-serif-lux text-lg uppercase tracking-[0.4em] text-amber-200/40 flex items-center gap-14">
            {t} <span className="text-amber-500/60 text-xs">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

const STATS = [
  { value: "2000+", label: "Vehicles Detailed" },
  { value: "4.9", label: "Average Rating", star: true },
  { value: "7-day", label: "Appointments" },
  { value: "On-site", label: "Water & Power" },
];

function StatsGrid() {
  return (
    <section data-testid="stats-section" className="border-b border-amber-500/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-2 lg:grid-cols-4 gap-y-12">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08} className={`text-center lg:text-left px-6 ${i > 0 ? "lg:border-l lg:border-zinc-800/80" : ""}`} data-testid={`stat-${i}`}>
            <p className="font-serif-lux gold-text text-5xl sm:text-6xl font-medium leading-none flex items-baseline justify-center lg:justify-start gap-1">
              {s.value}
              {s.star && <Star size={26} className="text-amber-400 self-center" fill="currentColor" strokeWidth={0} />}
            </p>
            <p className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 mt-3">{s.label}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Chapters() {
  return (
    <section id="craft" data-testid="manifesto-section" className="py-24 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Eyebrow>Our Craft</Eyebrow>
        <h2 className="font-serif-lux font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
          Artistry, <span className="gold-text italic">performed on wheels</span>
        </h2>
      </Reveal>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.1} className="border-t border-amber-500/20 pt-8" data-testid={`chapter-${c.n}`}>
            <span className="font-serif-lux italic text-amber-400/90 text-2xl">{c.n}</span>
            <h3 className="font-serif-lux font-semibold text-2xl sm:text-3xl mt-3 tracking-tight">{c.title}</h3>
            {c.sub && <p className="font-serif-lux italic gold-text text-lg mt-2">{c.sub}</p>}
            <p className="text-zinc-400 text-sm leading-relaxed mt-4 max-w-md">{c.text}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Packages() {
  const navigate = useNavigate();
  return (
    <section id="packages" data-testid="packages-section" className="py-24 lg:py-32 bg-[#0c0c0e] border-y border-amber-500/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>The Menu</Eyebrow>
            <h2 className="font-serif-lux font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Packages &amp; <span className="gold-text italic">pricing</span>
            </h2>
          </div>
          <p className="text-zinc-500 text-sm max-w-xs">Every package is performed at your location. Water, power and perfection — we bring it all.</p>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8">
          {PACKAGES.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.12}>
              <div
                data-testid={`package-card-${p.id}`}
                className={`gold-border-card p-8 h-full flex flex-col ${p.popular ? "border-amber-400/60 shadow-[0_0_40px_rgba(212,175,55,0.15)]" : ""}`}
              >
                {p.popular && (
                  <span className="absolute top-0 right-0 gold-foil text-black text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-1.5 flex items-center gap-1">
                    <Sparkles size={11} /> Most Loved
                  </span>
                )}
                <p className="font-mono-gold text-amber-400/80 text-[10px] tracking-[0.3em] uppercase">{p.duration}</p>
                <h3 className="font-serif-lux font-semibold text-3xl mt-3 tracking-tight">{p.name}</h3>
                <p className="text-zinc-500 text-sm mt-2 italic font-serif-lux text-base">{p.tagline}</p>
                <p className="mt-6">
                  <span className="font-serif-lux text-5xl font-semibold gold-text">${p.priceMin}–{p.priceMax}</span>
                  <span className="text-zinc-500 text-sm ml-2">/ vehicle</span>
                </p>
                <ul className="mt-8 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="text-sm text-zinc-300 flex gap-3">
                      <span className="text-amber-400 mt-0.5">✦</span> {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate(`/book?package=${p.id}`)}
                  data-testid={`package-book-${p.id}`}
                  className={`mt-10 w-full py-4 rounded-sm text-[11px] font-semibold uppercase tracking-[0.25em] transition-all duration-200 ${
                    p.popular ? "gold-foil text-black hover:scale-[1.02]" : "border border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
                  }`}
                >
                  Reserve This Detail
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const REVIEWS = [
  {
    name: "Marcus T.",
    service: "The Signature Detail",
    quote: "They pulled into my driveway at 8 AM and by lunch my Range Rover looked better than the day I collected it. The foam process alone was theatre.",
  },
  {
    name: "Priya S.",
    service: "The Interior Valet",
    quote: "Two kids, one dog, endless crumbs — gone. I never left my kitchen. This is how car care should work.",
  },
  {
    name: "Daniel R.",
    service: "The Premier Obsidian",
    quote: "The ceramic finish on my M4 still beads like mercury six months later. Worth every cent — my garage queen finally gets driven.",
  },
  {
    name: "Amber L.",
    service: "Gift Card Client",
    quote: "Sent my dad a gift card for Father's Day. He booked in two minutes and hasn't stopped talking about the wheel detailing since.",
  },
];

function Reviews() {
  return (
    <section id="reviews" data-testid="reviews-section" className="py-24 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <div className="absolute top-1/3 -left-32 w-[400px] h-[400px] rounded-full bg-amber-500/8 blur-[130px] ambient-glow pointer-events-none" />
      <Reveal className="text-center flex flex-col items-center">
        <Eyebrow center>Word From The Driveways</Eyebrow>
        <h2 className="font-serif-lux font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
          Trusted on <span className="gold-text italic">2,000+ cars</span>
        </h2>
        <div className="mt-8 inline-flex items-center gap-3 border border-amber-500/25 rounded-full px-6 py-3 bg-[#121215]" data-testid="reviews-aggregate">
          <Stars size={16} />
          <span className="font-serif-lux font-semibold gold-text text-3xl leading-none">4.9</span>
          <span className="text-zinc-500 text-[10px] uppercase tracking-[0.25em]">average rating</span>
        </div>
      </Reveal>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {REVIEWS.map((r, i) => (
          <Reveal key={r.name} delay={i * 0.1}>
            <div className="gold-border-card p-6 h-full flex flex-col" data-testid={`review-card-${i}`}>
              <Stars />
              <p className="text-zinc-300 text-sm leading-relaxed mt-4 flex-1 font-serif-lux text-lg italic">“{r.quote}”</p>
              <div className="mt-6 pt-4 border-t border-zinc-800">
                <p className="font-semibold text-sm tracking-wide uppercase">{r.name}</p>
                <p className="font-mono-gold text-amber-400/70 text-[10px] tracking-[0.25em] uppercase mt-1">{r.service}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function GalleryCard({ item, index }) {
  return (
    <Reveal delay={index * 0.08} className={item.wide ? "md:col-span-2" : ""}>
      <div
        data-testid={`gallery-card-${index}`}
        className="group relative overflow-hidden rounded-xl border border-amber-500/20 bg-[#141417] aspect-[4/3] md:aspect-auto md:h-[420px]"
      >
        <video
          src={item.src}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.12),transparent_60%)]" />
        <div className="absolute bottom-0 p-6">
          <p className="font-mono-gold text-amber-400/80 text-[10px] tracking-[0.3em] uppercase">{String(index + 1).padStart(2, "0")} — Live</p>
          <h3 className="font-serif-lux font-semibold tracking-tight text-2xl mt-1">{item.title}</h3>
          <p className="text-zinc-400 text-xs mt-1">{item.caption}</p>
        </div>
      </div>
    </Reveal>
  );
}

function Gallery() {
  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Eyebrow>The Studio In Motion</Eyebrow>
        <h2 className="font-serif-lux font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
          Watch the <span className="gold-text italic">craft</span>
        </h2>
      </Reveal>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
        {MEDIA.gallery.map((item, i) => (
          <GalleryCard key={item.title} item={item} index={i} />
        ))}
      </div>
    </section>
  );
}

function Garage() {
  return (
    <section id="garage" data-testid="garage-section" className="py-24 lg:py-32 bg-[#0c0c0e] border-y border-amber-500/10 relative overflow-hidden">
      <div className="absolute top-0 left-1/4 w-[400px] h-[400px] rounded-full bg-amber-500/8 blur-[130px] ambient-glow pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>From The Garage</Eyebrow>
          <h2 className="font-serif-lux font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Cars we <span className="gold-text italic">live with</span>
          </h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-5 gap-8 items-stretch">
          <Reveal className="lg:col-span-2">
            <div className="relative h-full" data-testid="garage-photo-frame">
              <div className="absolute -inset-3 border border-amber-500/25 rounded-2xl" />
              <div className="relative h-full overflow-hidden rounded-xl border border-amber-500/40 shadow-[0_30px_80px_rgba(212,175,55,0.12)] group">
                <img
                  src="/garage-supra-2.webp"
                  alt="The owner's personal Toyota GR Supra, ceramic maintained"
                  className="w-full h-full object-cover object-center min-h-[420px] transition-transform duration-700 group-hover:scale-105"
                  data-testid="garage-photo-supra"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 p-6">
                  <p className="font-mono-gold text-amber-400/80 text-[10px] tracking-[0.3em] uppercase">The Owner's Personal Car</p>
                  <h3 className="font-serif-lux font-semibold text-3xl mt-1">Toyota GR Supra</h3>
                  <p className="text-zinc-300 text-xs mt-1">Ceramic maintained in-house — our own test bed for every product we use</p>
                </div>
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              {
                img: "/gallery-foam.webp",
                title: "The Foam Bath",
                caption: "Snow-foam dwell on a client's Range Rover Sport — right in their driveway",
              },
              {
                img: "/gallery-finish.webp",
                title: "The Result",
                caption: "Deep obsidian gloss, dressed wheels — detailed where it parks",
              },
              {
                img: "/gallery-interior-a.webp",
                title: "The Interior Before",
                caption: "Ground-in dirt, hair & stains — exactly as it arrived",
              },
              {
                img: "/gallery-interior-b.webp",
                title: "The Interior After",
                caption: "Extracted, steamed & conditioned — showroom fresh",
              },
              {
                img: "/gallery-engine-a.webp",
                title: "The Engine Bay Before",
                caption: "Months of dust, grime & buildup under the hood",
              },
              {
                img: "/gallery-engine-b.webp",
                title: "The Engine Bay After",
                caption: "Degreased, detailed & dressed — factory fresh",
              },
              {
                img: "/gallery-rav4-a.webp",
                title: "The RAV4 Before",
                caption: "Winter salt & road film — as it pulled in",
              },
              {
                img: "/gallery-rav4-b.webp",
                title: "The RAV4 After",
                caption: "Foam-washed & hand-dried — gloss restored in the driveway",
              },
              {
                img: "/gallery-kia-a.webp",
                title: "The Cabin Before",
                caption: "Dusty mats & daily wear — as it arrived",
              },
              {
                img: "/gallery-kia-b.webp",
                title: "The Cabin After",
                caption: "Steam-cleaned & dressed — protected mats, showroom delivery",
              },
              {
                img: "/gallery-tesla-b.webp",
                title: "The Tesla Foam Bath",
                caption: "Snow-foam dwell pulling grime off a Model Y — mid-transformation",
              },
              {
                img: "/gallery-tesla-a.webp",
                title: "The Tesla After",
                caption: "Glossy, dressed & hand-dried — finished at the customer's curb",
              },
              {
                img: "/gallery-van-a.webp",
                title: "The Family Hauler Before",
                caption: "Crumbs, snacks & everyday chaos under the seats",
              },
              {
                img: "/gallery-van-b.webp",
                title: "The Family Hauler After",
                caption: "Vacuumed, extracted & detailed — every rail and crevice",
              },
              {
                img: "/gallery-crv-a.webp",
                title: "The Cockpit, Restored",
                caption: "Dash, console & crevices detailed — CR-V delivered fresh",
              },
              {
                img: "/gallery-crv-b.webp",
                title: "The Rear Cabin, Refreshed",
                caption: "Seats vacuumed & dressed — ready for the family",
              },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="group relative overflow-hidden rounded-xl border border-amber-500/20 bg-[#141417] h-64" data-testid={`garage-card-${i}`}>
                  <img src={c.img} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.12),transparent_60%)]" />
                  <div className="absolute bottom-0 p-5">
                    <h3 className="font-serif-lux font-semibold text-xl">{c.title}</h3>
                    <p className="text-zinc-400 text-xs mt-0.5">{c.caption}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function GiftTeaser() {
  const navigate = useNavigate();
  return (
    <section data-testid="gift-teaser-section" className="py-24 lg:py-32 bg-[#0c0c0e] border-y border-amber-500/10 relative overflow-hidden">
      <div className="absolute -top-40 right-0 w-[500px] h-[500px] rounded-full bg-amber-500/10 blur-[140px] ambient-glow" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <Eyebrow>The Gift of Shine</Eyebrow>
          <h2 className="font-serif-lux font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            A detail that parks in <span className="gold-text italic">their driveway</span>
          </h2>
          <p className="text-zinc-400 text-sm leading-relaxed mt-6 max-w-md">
            Send a digital gift card in seconds. Delivered by email with a golden voucher code, redeemable on any package, any time.
          </p>
          <button
            onClick={() => navigate("/gift-cards")}
            data-testid="gift-teaser-cta"
            className="mt-10 gold-foil text-black font-semibold uppercase tracking-[0.25em] text-[11px] px-9 py-4 rounded-sm hover:scale-[1.03] transition-transform flex items-center gap-2"
          >
            <Gift size={14} /> Send a Gift Card
          </button>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative mx-auto max-w-md">
            <motion.div
              whileHover={{ rotateY: -8, rotateX: 4 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
              className="rounded-2xl p-[1px] bg-gradient-to-br from-amber-400/60 via-yellow-200/20 to-transparent shadow-[0_30px_80px_rgba(212,175,55,0.15)]"
              style={{ transformStyle: "preserve-3d", perspective: 800 }}
            >
              <div className="rounded-2xl bg-[#101013] p-8 aspect-[8/5] flex flex-col justify-between">
                <div className="flex justify-between items-start">
                  <span className="font-serif-lux text-lg text-zinc-100">Valet <span className="gold-text italic">Premier</span></span>
                  <Gift className="text-amber-400" size={20} />
                </div>
                <div>
                  <p className="font-mono-gold text-amber-400/70 text-xs tracking-[0.3em]">VPD-••••-••••</p>
                  <p className="font-serif-lux text-5xl font-semibold gold-text mt-2">$100</p>
                </div>
                <p className="text-zinc-500 text-[10px] uppercase tracking-[0.3em]">Concierge Mobile Detailing</p>
              </div>
            </motion.div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "General Inquiry", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Enquiry received — we will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", topic: "General Inquiry", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        <Reveal>
          <Eyebrow>Concierge Line</Eyebrow>
          <h2 className="font-serif-lux font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Have a <span className="gold-text italic">special request?</span>
          </h2>
          <p className="text-zinc-400 text-sm leading-relaxed mt-6 max-w-md">
            Fleet bookings, wedding cars, concours prep — drop us a note and we'll reply personally.
          </p>
          <div className="mt-10 space-y-5">
            <a href="tel:+16814528599" data-testid="contact-phone-link" className="flex items-center gap-5 group">
              <span className="w-12 h-12 border border-amber-500/40 flex items-center justify-center text-amber-400 group-hover:bg-amber-500/10 transition-colors">
                <Phone size={18} strokeWidth={1.5} />
              </span>
              <span className="text-zinc-200 text-lg font-serif-lux tracking-wide group-hover:text-amber-200 transition-colors">(681) 452-8599</span>
            </a>
            <a href="mailto:Valetpremierdetailing@gmail.com" data-testid="contact-email-link" className="flex items-center gap-5 group">
              <span className="w-12 h-12 border border-amber-500/40 flex items-center justify-center text-amber-400 group-hover:bg-amber-500/10 transition-colors">
                <Mail size={18} strokeWidth={1.5} />
              </span>
              <span className="text-zinc-200 text-lg font-serif-lux tracking-wide group-hover:text-amber-200 transition-colors break-all">Valetpremierdetailing@gmail.com</span>
            </a>
            <div className="flex items-center gap-5">
              <span className="w-12 h-12 border border-amber-500/40 flex items-center justify-center text-amber-400">
                <MapPin size={18} strokeWidth={1.5} />
              </span>
              <span className="text-zinc-400 text-sm">Proudly serving Martinsburg and Berkeley, Jefferson, Washington &amp; Frederick Counties — 7 days a week</span>
            </div>
          </div>
          <div className="mt-12 relative max-w-sm" data-testid="contact-owner-photo-frame">
            <div className="absolute -inset-2 border border-amber-500/25 rounded-xl" />
            <div className="relative overflow-hidden rounded-lg border border-amber-500/40 shadow-[0_20px_60px_rgba(212,175,55,0.12)]">
              <img src="/owner.webp" alt="Pedro Peralta, Founder of Valet Premier Detailing" className="w-full aspect-[4/3] object-cover object-top" data-testid="contact-owner-photo" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 p-5">
                <p className="font-serif-lux italic gold-text text-xl">You'll deal with me personally</p>
                <p className="text-zinc-100 text-sm font-semibold tracking-wide mt-1" data-testid="owner-name">Pedro Peralta</p>
                <p className="text-zinc-300 text-[10px] uppercase tracking-[0.25em] mt-0.5">Founder &amp; Master Detailer</p>
              </div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <input className="vpd-input" placeholder="Full name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
            <input className="vpd-input" type="email" placeholder="Email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
            <input className="vpd-input" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone-input" />
            <select className="vpd-input" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="contact-topic-select">
              <option>General Inquiry</option>
              <option>Fleet Booking</option>
              <option>Wedding Cars</option>
              <option>Concours Prep</option>
              <option>Gift Cards</option>
            </select>
            <textarea className="vpd-input min-h-[130px]" placeholder="Tell us about your vehicle or event…" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" />
            <button type="submit" disabled={sending} data-testid="contact-submit-button" className="w-full gold-foil text-black font-semibold uppercase tracking-[0.25em] text-[11px] py-4 rounded-sm disabled:opacity-50 hover:scale-[1.01] transition-transform">
              {sending ? "Sending…" : "Send Enquiry"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <Hero />
      <Marquee />
      <StatsGrid />
      <Chapters />
      <Packages />
      <Reviews />
      <Gallery />
      <Garage />
      <GiftTeaser />
      <Contact />
    </main>
  );
}
